import { MODEL_LABELS } from './model.js';
import { CODEX_MODEL_LABELS } from './codex-model.js';
/**
 * 31/10/2026 (P0 be38f0d9, troca imediata) — o pedido padrão que a sessão recebe depois do Esc: salvar o estado viável
 * no lote e encerrar o turno, para o term-deck compactar e trocar logo em seguida, sem esperar standby nem fronteira.
 */
export function immediateSwitchPrompt(model, effort) {
    return `Troca de modelo/esforço: ${model}${effort ? ` ${effort}` : ''}; salve o estado viável no lote e encerre o turno`;
}
/** Marcas de compactação visíveis na tela (Claude Code e Codex), contadas antes e depois do `/compact` quando não há conversa nativa local. */
export function compactMarksOnScreen(screen) {
    return (screen.match(/Conversation\s+compacted|Context\s+compacted|Compacted\s*\(\s*ctrl/gi) ?? []).length;
}
/**
 * Sinais de que o `/compact` chegou à CLI: o eco do comando digitado (`❯ /compact`) ou o aviso de compactação em curso.
 * Compara-se a contagem antes e depois do envio; sessão ociosa sem aumento é comando que nunca chegou (03/11, 0.522.344).
 */
export function compactCommandOnScreen(screen) {
    return (screen.match(/^\s*(?:[❯>›]\s*)?\/compact\b|Compacting\s+(?:conversation|context)/gim) ?? []).length;
}
/** Confirmação do `/effort <nível>` do Claude Code ("Set effort level to high…"); compara-se a contagem antes e depois. */
export function claudeEffortConfirmed(screen, effort) {
    // 31/10/2026 (ensaio do nível 1): o eco do próprio comando (`❯ /effort medium`) e a recusa da CLI casavam como confirmação e a
    // transição saía `applied` sem `effort_unconfirmed` com o esforço intacto na barra. Eco de comando e recusa não confirmam.
    if (/^\s*(?:[❯>›]\s*)?\/effort\b/i.test(screen) || claudeEffortRefused(screen))
        return false;
    const expected = effort === 'xhigh' ? '(?:xhigh|extra[ \\t]+high)' : effort.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`effort(?:[ \\t]+level)?(?:[ \\t]+set)?(?:[ \\t]+to|[ \\t]*:)?[ \\t]+${expected}(?![a-z])`, 'i').test(screen);
}
export function claudeEffortConfirmations(screen, effort) {
    return screen.split('\n').filter((line) => claudeEffortConfirmed(line, effort)).length;
}
/**
 * `CLAUDE_CODE_EFFORT_LEVEL` (injetada por spawn pelo term-deck) trava o `/effort` da TUI: a CLI responde "CLAUDE_CODE_EFFORT_LEVEL=high
 * overrides this session — clear it and medium takes over" e o esforço da sessão não muda. A recusa é um fato próprio, nunca confirmação.
 */
export function claudeEffortRefused(screen) {
    return /overrides\s+this\s+session|not\s+applied.{0,80}overrides/i.test(screen);
}
export function claudeEffortRefusals(screen) {
    return screen.split('\n').filter((line) => claudeEffortRefused(line)).length;
}
export function modelSwitchPending(value) {
    return !!value && value.phase !== 'applied' && value.phase !== 'blocked';
}
/** Correio segurado à espera da fronteira: depois disso ele volta a sair e a troca segue com o próprio prazo. */
export const MODEL_SWITCH_MAIL_HOLD_WAITING_MS = 10 * 60_000;
/** Correio segurado com a troca já em curso (compactação, seletor, verificação), contado do último passo. */
export const MODEL_SWITCH_MAIL_HOLD_ACTIVE_MS = 15 * 60_000;
/**
 * 8d655e8d (21/10, transição ee154d87) — o correio nativo (fila do Codex, pipe do Claude) não passa pelo bloqueio de
 * escrita da troca de modelo: cada fim de turno recebia a próxima mensagem e a sessão nunca ficava ociosa os 30 s da
 * fronteira. Até quando o correio segura a entrega a esta sessão; null = entrega normal.
 */
export function modelSwitchMailHoldUntil(value, now) {
    if (!value || !modelSwitchPending(value))
        return null;
    const until = value.phase === 'waiting'
        ? value.requestedAt + MODEL_SWITCH_MAIL_HOLD_WAITING_MS
        : value.updatedAt + MODEL_SWITCH_MAIL_HOLD_ACTIVE_MS;
    return until > now ? until : null;
}
/** Match only native, top-level records; quoted prompts/tool output cannot attest compaction. */
export function nativeCompactCompleted(line, provider) {
    try {
        const row = JSON.parse(line);
        return provider === 'codex'
            ? row.type === 'compacted'
            : row.type === 'system' && row.subtype === 'compact_boundary';
    }
    catch {
        return false;
    }
}
function modelLabels(model) {
    return [...new Set([model, MODEL_LABELS[model], CODEX_MODEL_LABELS[model]]
            .filter((label) => !!label))];
}
function escapedLabel(label) {
    return label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function reasoningPickerForModel(screen, model) {
    return modelLabels(model).some(label => new RegExp(`^\\s*Select Reasoning Level for ${escapedLabel(label)}\\s*$`, 'mi').test(screen));
}
/** Numeric shortcuts come from the currently rendered picker, never from a cached catalog order. */
export function modelPickerChoice(screen, model) {
    if (!screen.includes('Select Model and Effort'))
        return null;
    const rows = [...screen.matchAll(/^\s*[›>]?\s*([1-9])\.\s+([^\r\n]+)/gm)];
    // CLI0.164.0 renders Terra with its catalog display label, including a space.
    // Accept only exact known labels/IDs with a boundary; duplicate rows stay ambiguous.
    const labels = modelLabels(model).map(label => new RegExp(`^${escapedLabel(label)}(?=[\\s(]|$)`, 'i'));
    const matches = rows.filter(row => labels.some(label => label.test(row[2] ?? '')));
    return matches.length === 1 ? matches[0][1] : null;
}
export function effortPickerChoice(screen, model, effort, advancedRequested = false) {
    const ordinary = reasoningPickerForModel(screen, model);
    const advanced = advancedRequested && ['max', 'ultra'].includes(effort) && /^\s*Advanced Reasoning\s*$/m.test(screen);
    if (!ordinary && !advanced)
        return null;
    const label = {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        xhigh: 'Extra high',
        max: 'Max',
        ultra: 'Ultra',
    }[effort];
    if (!label)
        return null;
    const rows = [...screen.matchAll(/^\s*([›>]?)\s*([1-9])\.\s+(.+)$/gm)];
    const choices = rows.filter((row) => row[3] === label || row[3].startsWith(`${label} `));
    if (choices.length !== 1)
        return null;
    // New CLI offers Enter for the GLOBAL default. Move the focused row, then use
    // the explicit session-only key, including the separate Advanced Reasoning menu.
    if (/enter\s+default\s*·\s*s\s+session\s*·\s*esc\s+back/i.test(screen)) {
        const focused = rows.filter(row => !!row[1]);
        if (focused.length !== 1)
            return null;
        const delta = rows.indexOf(choices[0]) - rows.indexOf(focused[0]);
        return (delta < 0 ? '\u001b[A'.repeat(-delta) : '\u001b[B'.repeat(delta)) + 's';
    }
    // The advanced submenu has no model identity: never commit it without its
    // session-only footer and the controller's preceding More reasoning action.
    return advanced ? null : choices[0][2];
}
export function moreEffortPickerChoice(screen, model, effort) {
    if (!['max', 'ultra'].includes(effort) ||
        !reasoningPickerForModel(screen, model))
        return null;
    const rows = [...screen.matchAll(/^\s*[›>]?\s*([1-9])\.\s+More reasoning.*$/gm)];
    return rows.length === 1 ? rows[0][1] : null;
}
export function modelSwitchConfirmed(screen, model, effort) {
    const labels = modelLabels(model);
    return labels.some((label) => {
        const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`(?:Model changed to|Switched to|Set model to)\\s+${escaped}(?=[\\s(]|$)([^\\n]*)`, 'gi');
        for (const match of screen.matchAll(pattern)) {
            if (effort === undefined)
                return true;
            const expected = effort === 'xhigh'
                ? '(?:xhigh|extra[ \\t]+high)'
                : effort.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (new RegExp(`^[ \\t(]*${expected}(?=[ \\t)]|$)`, 'i').test(match[1] ?? ''))
                return true;
        }
        return false;
    });
}
//# sourceMappingURL=session-model-switch.js.map